import { useSyncExternalStore } from "react";

/** Sidebar tab: the chat list, the space's routines, or the selected bot's computer. */
export type SidebarTab = "chat" | "routines" | "computer";

/** Shell commands the desktop wrapper and the keyboard shortcuts trigger. None of them writes data. */
export type LayoutActions = {
  openSearch: () => void;
  toggleSidebar: () => void;
  newChat: () => void;
};

type LayoutState = { tab: SidebarTab };

const TAB_KEY_PREFIX = "rakazo:sidebar-tab:";
const TABS: readonly SidebarTab[] = ["chat", "routines", "computer"];

let state: LayoutState = { tab: "chat" };
let actions: Partial<LayoutActions> = {};
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reads the remembered tab for this account; anything unknown falls back to the chat tab. */
export function readSidebarTab(userId: string | null | undefined): SidebarTab {
  if (!userId) return "chat";
  try {
    const saved = window.localStorage.getItem(`${TAB_KEY_PREFIX}${userId}`);
    return TABS.includes(saved as SidebarTab) ? (saved as SidebarTab) : "chat";
  } catch {
    return "chat";
  }
}

/** Switches the sidebar tab and remembers it per account (only the tab name is stored). */
export function setSidebarTab(tab: SidebarTab, userId?: string | null): void {
  if (state.tab === tab) return;
  state = { ...state, tab };
  if (userId) {
    try {
      if (tab === "chat") window.localStorage.removeItem(`${TAB_KEY_PREFIX}${userId}`);
      else window.localStorage.setItem(`${TAB_KEY_PREFIX}${userId}`, tab);
    } catch {
      // Private mode or full storage: the tab still switches for this session.
    }
  }
  emit();
}

/** Current sidebar tab, re-rendering when it changes. */
export function useSidebarTab(): SidebarTab {
  return useSyncExternalStore(
    subscribe,
    () => state.tab,
    () => "chat" as const,
  );
}

/** The shell registers its handlers here; returns the cleanup that removes them again. */
export function registerLayoutActions(next: LayoutActions): () => void {
  actions = next;
  return () => {
    if (actions === next) actions = {};
  };
}

/** Opens the Ctrl/⌘+K search overlay. */
export function openSearch(): void {
  actions.openSearch?.();
}

/** Collapses or expands the sidebar (Ctrl/⌘+B). */
export function toggleSidebar(): void {
  actions.toggleSidebar?.();
}

/** Opens the menu for a new chat (Ctrl/⌘+N in the desktop app). */
export function newChat(): void {
  actions.newChat?.();
}

/** Resets the store between tests. */
export function resetLayoutForTests(): void {
  state = { tab: "chat" };
  actions = {};
  listeners.clear();
}
